import ContentIllustration from '../../components/ui/ContentIllustration';
import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, RotateCcw, AlertTriangle, Loader2,
    Home, FileText, X, Gift, Trophy, Copy, Check, Sparkles,
} from 'lucide-react';
import publicApi from '../../services/publicApi';
import usePageTitle from '../../hooks/usePageTitle';
import QuestionCard from '../../components/quiz/QuestionCard';
import ScoreCelebration from '../../components/quiz/ScoreCelebration';
import RichContent from '../../components/content/RichContent';
import ProgressBar from '../../components/ui/ProgressBar';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { FaviconMark } from '../../components/ui/BrandLogo';
import DocumentViewer from '../../components/ui/DocumentViewer';

/**
 * Public "open training" quiz — reached via /open?code=XXXX-XXXXX.
 * No auth, no tracking. Apprentissage mode : correction + explication
 * shown right after each answer. Visual language matches QuizCodePlayer.
 */
export default function OpenQuiz() {
    usePageTitle('Entraînement libre');
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const urlCode = (params.get('code') || '').toUpperCase();

    const [codeInput, setCodeInput] = useState(urlCode);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [finished, setFinished] = useState(false);
    const [showDoc, setShowDoc] = useState(false);
    // Mode défi (campagne Open avec cadeau) : écran de pré-start + scoring serveur.
    const [challengeStarted, setChallengeStarted] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [claimResult, setClaimResult] = useState(null);
    const [codeCopied, setCodeCopied] = useState(false);
    const dotsRef = useRef(null);
    // Fire-and-forget pings : 1x "started" on first answer, 1x "completed" on finished screen.
    const pingSentRef = useRef({ started: false, completed: false });
    const sendPing = useCallback((event) => {
        if (!data || pingSentRef.current[event]) return;
        pingSentRef.current[event] = true;
        const code = urlCode || codeInput;
        if (!code) return;
        publicApi.post('/open-quiz/ping.php', { code, event }).catch(() => {
            // Allow retry on next session if the network glitched.
            pingSentRef.current[event] = false;
        });
    }, [data, urlCode, codeInput]);

    // Tracking granulaire (anonyme) : 1 session = 1 uuid client. Pings start /
    // answer / complete vers track.php (best-effort, jamais bloquant).
    const sessionUidRef = useRef(null);
    const trackStartedRef = useRef(false);
    const trackCompleteRef = useRef(false);
    const qShownAtRef = useRef(0);
    const track = useCallback((event, extra = {}) => {
        const code = urlCode || codeInput;
        if (!code || !sessionUidRef.current) return;
        publicApi.post('/open-quiz/track.php', { code, session_uid: sessionUidRef.current, event, ...extra }).catch(() => {});
    }, [urlCode, codeInput]);

    const loadCode = useCallback(async (code) => {
        if (!code) return;
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await publicApi.get('/open-quiz/start.php', { params: { code } });
            setData(res.data);
            setAnswers({});
            setCurrentIndex(0);
            setFinished(false);
            setChallengeStarted(false);
            setClaiming(false);
            setClaimResult(null);
            setCodeCopied(false);
            pingSentRef.current = { started: false, completed: false };
            // Nouvelle session de tracking à chaque (re)lancement.
            sessionUidRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            trackStartedRef.current = false;
            trackCompleteRef.current = false;
            qShownAtRef.current = Date.now();
        } catch (e) {
            setError(e.response?.data?.message || 'Code inconnu');
            setData(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => { if (urlCode) loadCode(urlCode); }, [urlCode, loadCode]);
    // Chronomètre par question (pour response_time du tracking).
    useEffect(() => { qShownAtRef.current = Date.now(); }, [currentIndex]);

    const questions = data?.questions || [];
    const total = questions.length;
    const currentQ = questions[currentIndex];
    const given = currentQ ? answers[currentQ.id] : null;
    const correct = currentQ?.bonne_reponse;
    const answered = !!given;

    const setAnswer = (qId, aId) => {
        sendPing('started');
        const giftMode = !!data?.is_gift_challenge;
        // Première réponse : ouvre la session de tracking avec le contexte marketing.
        if (!trackStartedRef.current) {
            trackStartedRef.current = true;
            const utm = params.get('utm_source') || '';
            let ref = '';
            try { if (document.referrer && new URL(document.referrer).origin !== window.location.origin) ref = document.referrer; } catch { /* ignore */ }
            track('start', { total, utm_source: utm, referrer: ref });
        }
        const q = questions.find(x => x.id === qId);
        const pos = questions.findIndex(x => x.id === qId) + 1;
        // En mode concours, le client ne connaît pas la bonne réponse → is_correct null.
        const isCorrect = giftMode ? null : (q && aId === q.bonne_reponse ? 1 : 0);
        let rt = null;
        if (qShownAtRef.current) {
            const s = Math.round((Date.now() - qShownAtRef.current) / 1000);
            if (s >= 1 && s <= 600) rt = s;
        }
        track('answer', { question_id: qId, position: pos, is_correct: isCorrect, response_time: rt });
        setAnswers(prev => ({ ...prev, [qId]: aId }));
    };
    const goNext = () => currentIndex < total - 1 ? setCurrentIndex(i => i + 1) : setFinished(true);
    useEffect(() => {
        if (!finished) return;
        sendPing('completed');
        // Mode défi : le "complete" granulaire est envoyé après le scoring serveur (claim).
        if (data?.is_gift_challenge || trackCompleteRef.current) return;
        trackCompleteRef.current = true;
        const nbCorrect = questions.reduce((a, q) => a + (answers[q.id] === q.bonne_reponse ? 1 : 0), 0);
        const pct = total ? Math.round((nbCorrect / total) * 100) : 0;
        track('complete', { score: nbCorrect, pct });
    }, [finished, sendPing, data, questions, answers, total, track]);

    // Mode défi : à l'écran final, on envoie les réponses au serveur qui calcule
    // le score (les bonnes réponses ne sont jamais exposées au client) et génère
    // un code cadeau si 100% sous le plafond de gagnants.
    useEffect(() => {
        if (!finished || !data?.is_gift_challenge) return;
        if (claimResult || claiming) return;
        setClaiming(true);
        const code = urlCode || codeInput;
        publicApi.post('/open-quiz/claim.php', { code, answers })
            .then(({ data: res }) => {
                setClaimResult(res.data);
                // "complete" granulaire avec le score validé serveur.
                if (!trackCompleteRef.current) {
                    trackCompleteRef.current = true;
                    track('complete', { score: res.data?.score ?? 0, pct: res.data?.pct ?? 0, won: res.data?.won ? 1 : 0 });
                }
            })
            .catch(e => setClaimResult({
                error: e.response?.data?.message || 'Validation impossible',
                score: 0, total, pct: 0, won: false,
            }))
            .finally(() => setClaiming(false));
    }, [finished, data, claimResult, claiming, answers, urlCode, codeInput, total, track]);
    const goPrev = () => { if (currentIndex > 0) setCurrentIndex(i => i - 1); };
    const goToQuestion = (i) => { if (i >= 0 && i < total) setCurrentIndex(i); };
    const restart = () => { loadCode(urlCode || codeInput); };
    // Close X : bring the user back to the code-entry screen. We clear the
    // loaded quiz state AND navigate to /open (no query) so `urlCode` is
    // empty at the next render → enter-code form appears.
    const handleBack = () => {
        setData(null);
        setAnswers({});
        setCurrentIndex(0);
        setFinished(false);
        setError(null);
        setCodeInput('');
        navigate('/open', { replace: true });
    };
    const scrollDots = (dir) => {
        if (!dotsRef.current) return;
        dotsRef.current.scrollBy({ left: dir === 'left' ? -120 : 120, behavior: 'smooth' });
    };

    // --- ENTER CODE (no URL code yet) ---
    if (!urlCode && !data) {
        return (
            <div className="public-code-page">
                <div className="public-code-bg-overlay" />
                <ThemeToggle className="cc-theme-toggle" />
                <div className="public-code-inner">
                    <h1 className="public-code-title">Entraînement libre</h1>
                    <div className="public-code-card">
                        <div style={{ textAlign: 'center', marginBottom: 22 }}>
                            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(124,58,237,0.12)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                                <ContentIllustration name="file-text" size={48} />
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: 0 }}>
                                Entrez le code d'entraînement qui vous a été communiqué.
                            </p>
                        </div>
                        <input type="text" value={codeInput}
                            onChange={e => setCodeInput(e.target.value.toUpperCase())}
                            onKeyDown={e => e.key === 'Enter' && loadCode(codeInput)}
                            placeholder="OPN-XXXXX-XXXXX" maxLength={20}
                            style={{ width: '100%', padding: '12px 14px', fontSize: '1.05em', border: '2px solid var(--card-border)', borderRadius: 12, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center', fontFamily: 'monospace', fontWeight: 600 }} />
                        {error && <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 6, fontSize: '0.85em' }}><AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{error}</div>}
                        <button className="btn btn-primary" style={{ marginTop: 16, width: '100%' }}
                            onClick={() => loadCode(codeInput)}
                            disabled={!codeInput.trim() || loading}>
                            {loading ? <Loader2 size={16} className="spin" /> : 'Démarrer l\'entraînement'}
                        </button>
                        <p style={{ fontSize: '0.72em', color: 'var(--text-muted)', marginTop: 14, textAlign: 'center' }}>
                            Aucune inscription, aucune donnée collectée.
                        </p>
                    </div>
                </div>
                <footer className="public-code-footer">
                    <nav className="public-code-footer-links">
                        <Link to="/mentions-legales">Mentions légales</Link>
                        <Link to="/cgv">CGV</Link>
                        <Link to="/confidentialite">Confidentialité</Link>
                        <a href="mailto:contact@monquiz.fr">Contact</a>
                    </nav>
                    <span className="public-code-footer-copy">© {new Date().getFullYear()} MonQuiz™ — Tous droits réservés</span>
                </footer>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="quiz-modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader2 size={28} className="spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="quiz-modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <div style={{ maxWidth: 420, padding: 24, background: 'var(--card-bg)', borderRadius: 16, border: '1px solid var(--card-border)', textAlign: 'center' }}>
                    <AlertTriangle size={32} style={{ color: '#ef4444', margin: '0 auto 10px' }} />
                    <h2>Code invalide</h2>
                    <p style={{ color: 'var(--text-muted)' }}>{error}</p>
                    <Link to="/" className="btn btn-secondary" style={{ marginTop: 12 }}><Home size={14} /> Retour à l'accueil</Link>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const design = data.design || {};
    const bgImage = design.category_image || design.header_image;
    const themeColor = design.couleur_theme || '#7c3aed';
    const sousSujet = data.sous_sujet || {};
    const docPath = sousSujet.doc_path;
    // Campaign teaser (admin "Open campaign" on editorial content) → push signup
    // at the end, tagged for acquisition attribution (utm + ?from=).
    const isCampaign = !!data.is_campaign;
    const campaignFrom = data.campaign?.code ? `open-${String(data.campaign.code).toLowerCase()}` : 'open';
    const scopeSlug = design.scope_slug;
    const scopeLabel = design.scope_label;

    // Mode défi : campagne Open avec cadeau (1 mois offert si 100%). Concours sans
    // correction pendant le jeu, scoring + génération du code côté serveur.
    const isGiftChallenge = !!data.is_gift_challenge;
    const giftOpen = data.gift_open !== false; // false uniquement si plafond gagnants atteint
    const dureeLabel = (d) => ({ '1m': '1 mois', '6m': '6 mois', '12m': '12 mois' }[d] || d || '1 mois');
    const giftDureeLabel = dureeLabel(data.gift_duree);

    // --- CHALLENGE INTRO (pré-start, mode défi) ---
    if (isGiftChallenge && !challengeStarted && !finished) {
        return (
            <div className={`quiz-modal-overlay ${bgImage ? 'has-bg-image' : ''}`} style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}>
                <button className="quiz-modal-close quiz-modal-close--fixed" onClick={handleBack} title="Fermer">
                    <X size={22} strokeWidth={2} />
                </button>
                <ThemeToggle className="quiz-theme-toggle" />
                <div className="quiz-page">
                <div className="quiz-page-scroll">
                    {(design.header_image || design.scope_label) && (
                        <div className="quiz-scope-banner" style={{ '--scope-accent': themeColor }}>
                            {design.header_image ? (
                                <div className="quiz-scope-banner-hero">
                                    <img src={design.header_image} alt="" className="quiz-scope-banner-img" />
                                    <div className="quiz-scope-banner-gradient" />
                                </div>
                            ) : (
                                <div className="quiz-scope-banner-hero" style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}dd)`, minHeight: 60 }} />
                            )}
                            <div className="quiz-scope-banner-content">
                                <div className="quiz-scope-banner-info">
                                    <span className="quiz-scope-banner-title">{design.scope_label}</span>
                                </div>
                                {sousSujet.categorie && <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>}
                            </div>
                        </div>
                    )}
                    <div style={{ padding: '24px 18px 20px', textAlign: 'center', maxWidth: 540, margin: '0 auto' }}>
                        <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg, #ec4899, #f59e0b)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxShadow: '0 8px 24px rgba(236,72,153,0.35)' }}>
                            <Trophy size={36} />
                        </div>
                        <h1 style={{ fontSize: '1.5em', fontWeight: 800, margin: '0 0 6px', lineHeight: 1.2 }}>
                            Défi 100% = {giftDureeLabel} offert{giftDureeLabel === '1 mois' ? '' : 's'} 🎁
                        </h1>
                        <p style={{ fontSize: '0.95em', color: 'var(--text)', margin: '0 0 4px', fontWeight: 600 }}>
                            {sousSujet.label || 'Quiz'}
                        </p>
                        {giftOpen ? (
                            <p style={{ fontSize: '0.92em', color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.55 }}>
                                Réponds correctement à <strong>100% des questions</strong> et on t'offre{' '}
                                <strong style={{ color: themeColor }}>{giftDureeLabel} d'abonnement</strong>
                                {scopeLabel ? <> sur la thématique <strong>{scopeLabel}</strong></> : null} 🎉
                            </p>
                        ) : (
                            <p style={{ fontSize: '0.9em', color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.55 }}>
                                Tous les cadeaux de ce défi ont déjà été remportés 🏁 — mais tu peux toujours
                                relever le challenge pour le plaisir (et découvrir la thématique).
                            </p>
                        )}

                        <div style={{ margin: '18px auto 0', maxWidth: 420, textAlign: 'left', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '14px 16px', fontSize: '0.85em', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                                <Sparkles size={15} style={{ color: themeColor, flexShrink: 0, marginTop: 2 }} />
                                <span><strong style={{ color: 'var(--text)' }}>Mode concours :</strong> les corrections sont masquées pendant le quiz, ton score est validé à la fin.</span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                                <FileText size={15} style={{ color: themeColor, flexShrink: 0, marginTop: 2 }} />
                                <span><strong style={{ color: 'var(--text)' }}>{total} question{total > 1 ? 's' : ''}</strong> — il faut un sans-faute pour gagner.</span>
                            </div>
                            {giftOpen && (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                    <Gift size={15} style={{ color: themeColor, flexShrink: 0, marginTop: 2 }} />
                                    <span>Si tu réussis, tu reçois un code à activer avec un <strong style={{ color: 'var(--text)' }}>compte gratuit</strong> (aucune carte bancaire).</span>
                                </div>
                            )}
                        </div>

                        <button className="btn btn-primary" style={{ marginTop: 20, minWidth: 240, fontSize: '1.02em' }}
                            onClick={() => setChallengeStarted(true)}>
                            <Trophy size={16} /> Relever le défi
                        </button>
                        {docPath && (
                            <div style={{ marginTop: 12 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setShowDoc(true)}>
                                    <FileText size={14} /> Consulter le document
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                </div>
                {showDoc && docPath && createPortal(
                    <DocumentViewer src={docPath} name={sousSujet.doc_name} onClose={() => setShowDoc(false)} />,
                    document.body
                )}
            </div>
        );
    }

    // --- FINISHED SCREEN — mode défi (scoring serveur + récompense) ---
    if (finished && isGiftChallenge) {
        const challengeFrom = `${campaignFrom}-defi`;
        const copyGiftCode = () => {
            if (!claimResult?.gift_code) return;
            navigator.clipboard.writeText(claimResult.gift_code);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 1800);
        };
        return (
            <div className={`quiz-modal-overlay ${bgImage ? 'has-bg-image' : ''}`} style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}>
                <button className="quiz-modal-close quiz-modal-close--fixed" onClick={handleBack} title="Fermer">
                    <X size={22} strokeWidth={2} />
                </button>
                <ThemeToggle className="quiz-theme-toggle" />
                <div className="quiz-page">
                <div className="quiz-page-scroll">
                    {(design.header_image || design.scope_label) && (
                        <div className="quiz-scope-banner" style={{ '--scope-accent': themeColor }}>
                            {design.header_image ? (
                                <div className="quiz-scope-banner-hero">
                                    <img src={design.header_image} alt="" className="quiz-scope-banner-img" />
                                    <div className="quiz-scope-banner-gradient" />
                                </div>
                            ) : (
                                <div className="quiz-scope-banner-hero" style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}dd)`, minHeight: 60 }} />
                            )}
                            <div className="quiz-scope-banner-content">
                                <div className="quiz-scope-banner-info">
                                    <span className="quiz-scope-banner-title">{design.scope_label}</span>
                                </div>
                                {sousSujet.categorie && <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>}
                            </div>
                        </div>
                    )}
                    <div style={{ padding: '24px 16px 16px', textAlign: 'center', maxWidth: 540, margin: '0 auto' }}>
                        {claiming || !claimResult ? (
                            <div style={{ padding: '40px 0', color: 'var(--text-muted)' }}>
                                <Loader2 size={30} className="spin" />
                                <div style={{ marginTop: 12, fontSize: '0.9em' }}>Validation de ton score…</div>
                            </div>
                        ) : claimResult.error ? (
                            <>
                                <AlertTriangle size={30} style={{ color: '#ef4444', margin: '0 auto 8px' }} />
                                <div style={{ fontSize: '0.92em', color: 'var(--text)', marginBottom: 14 }}>{claimResult.error}</div>
                                <button className="btn btn-primary" onClick={restart}><RotateCcw size={14} /> Réessayer</button>
                            </>
                        ) : (
                            <>
                                <ScoreCelebration pct={claimResult.pct} correct={claimResult.score} total={claimResult.total} color={themeColor} />
                                <div style={{ height: 16 }} />
                                {claimResult.won ? (
                                    <>
                                        <div style={{ fontSize: '1.15em', fontWeight: 800, color: '#16a34a', marginBottom: 6 }}>
                                            🎉 Sans-faute ! Tu as gagné {dureeLabel(claimResult.gift_duree)} offert{dureeLabel(claimResult.gift_duree) === '1 mois' ? '' : 's'} !
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginBottom: 14 }}>
                                            Voici ton code cadeau{scopeLabel ? <> pour <strong>{scopeLabel}</strong></> : null}. Active-le avec un compte gratuit.
                                        </div>
                                        <button onClick={copyGiftCode} title="Copier le code"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'monospace', fontSize: '1.35em', fontWeight: 800, letterSpacing: 2, color: '#fff', background: 'linear-gradient(135deg, #ec4899, #f59e0b)', border: 'none', borderRadius: 12, padding: '12px 20px', cursor: 'pointer', boxShadow: '0 6px 18px rgba(236,72,153,0.3)' }}>
                                            {claimResult.gift_code}
                                            {codeCopied ? <Check size={18} /> : <Copy size={18} />}
                                        </button>
                                        <div style={{ fontSize: '0.74em', color: 'var(--text-muted)', margin: '8px 0 18px' }}>
                                            {codeCopied ? 'Code copié !' : 'Clique pour copier — note-le bien.'}
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <Link to={`/inscription?from=${challengeFrom}`} className="btn btn-primary">
                                                <Gift size={15} /> Créer mon compte gratuit
                                            </Link>
                                            <Link to={`/compte/code?code=${claimResult.gift_code}`} className="btn btn-secondary">
                                                J'ai déjà un compte
                                            </Link>
                                        </div>
                                        <div style={{ fontSize: '0.72em', color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
                                            Pour activer : crée ton compte gratuit, puis va dans <strong>Mon compte › Activer un code cadeau</strong>.
                                        </div>
                                    </>
                                ) : claimResult.capped ? (
                                    <>
                                        <div style={{ fontSize: '1.05em', fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                                            Bravo, 100% ! 🎯
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginBottom: 16 }}>
                                            Tous les cadeaux du défi ont déjà été remportés — mais tu peux profiter de monquiz gratuitement.
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <Link to={`/inscription?from=${challengeFrom}`} className="btn btn-primary">Créer mon compte gratuit</Link>
                                            {scopeSlug && <Link to={`/${scopeSlug}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">Voir la thématique{scopeLabel ? ` ${scopeLabel}` : ''}</Link>}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ fontSize: '1.05em', fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                                            {claimResult.pct >= 80 ? 'Tu y es presque !' : 'Bien joué !'} {claimResult.score}/{claimResult.total}
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginBottom: 16 }}>
                                            Le cadeau se débloque à <strong>100%</strong>. Rejoue pour viser le sans-faute 💪
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button className="btn btn-primary" onClick={restart}><RotateCcw size={14} /> Rejouer le défi</button>
                                            <Link to={`/inscription?from=${challengeFrom}`} className="btn btn-secondary">Créer mon compte gratuit</Link>
                                        </div>
                                    </>
                                )}
                                <div style={{ marginTop: 14 }}>
                                    <Link to="/" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm"><FaviconMark size={16} /> Découvrir MonQuiz.fr</Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                </div>
            </div>
        );
    }

    // --- FINISHED SCREEN ---
    if (finished) {
        const nbAnswered = Object.keys(answers).length;
        const nbCorrect = questions.reduce((acc, q) => acc + (answers[q.id] === q.bonne_reponse ? 1 : 0), 0);
        const pct = total > 0 ? Math.round((nbCorrect / total) * 100) : 0;
        return (
            <div className={`quiz-modal-overlay ${bgImage ? 'has-bg-image' : ''}`} style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}>
                <button className="quiz-modal-close quiz-modal-close--fixed" onClick={handleBack} title="Fermer">
                    <X size={22} strokeWidth={2} />
                </button>
                <ThemeToggle className="quiz-theme-toggle" />
                <div className="quiz-page">
                <div className="quiz-page-scroll">
                    {(design.header_image || design.scope_label) && (
                        <div className="quiz-scope-banner" style={{ '--scope-accent': themeColor }}>
                            {design.header_image ? (
                                <div className="quiz-scope-banner-hero">
                                    <img src={design.header_image} alt="" className="quiz-scope-banner-img" />
                                    <div className="quiz-scope-banner-gradient" />
                                </div>
                            ) : (
                                <div className="quiz-scope-banner-hero" style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}dd)`, minHeight: 60 }} />
                            )}
                            <div className="quiz-scope-banner-content">
                                <div className="quiz-scope-banner-info">
                                    <span className="quiz-scope-banner-title">{design.scope_label}</span>
                                </div>
                                {sousSujet.categorie && <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>}
                            </div>
                        </div>
                    )}
                    <div style={{ padding: '24px 16px 16px', textAlign: 'center' }}>
                        <ScoreCelebration pct={pct} correct={nbCorrect} total={total} color={themeColor} />
                        <div style={{ height: 14 }} />
                        {isCampaign ? (
                            <>
                                <div style={{ fontSize: '0.95em', color: 'var(--text)', marginBottom: 6, fontWeight: 600 }}>
                                    Tu veux suivre ta progression et t'entraîner sur toute la thématique ?
                                </div>
                                <div style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginBottom: 16 }}>
                                    Crée ton compte gratuit — plusieurs sujets d'essai offerts, sans carte bancaire.
                                </div>
                                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Link to={`/inscription?from=${campaignFrom}`} className="btn btn-primary">
                                        Créer mon compte gratuit
                                    </Link>
                                    {scopeSlug && (
                                        <Link to={`/${scopeSlug}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                                            Voir la thématique{scopeLabel ? ` ${scopeLabel}` : ''}
                                        </Link>
                                    )}
                                </div>
                                <div style={{ marginTop: 10 }}>
                                    <button className="btn btn-ghost btn-sm" onClick={restart}><RotateCcw size={14} /> Recommencer</button>
                                    <Link to="/" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }}><FaviconMark size={16} /> Découvrir MonQuiz.fr</Link>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginBottom: 16 }}>
                                    <strong>Aucun résultat n'a été sauvegardé</strong> — c'est du pur entraînement.
                                </div>
                                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button className="btn btn-primary" onClick={restart}><RotateCcw size={14} /> Recommencer</button>
                                    <Link to="/" className="btn btn-secondary"><Home size={14} /> Accueil</Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                </div>
            </div>
        );
    }

    // --- QUIZ PLAYER (apprentissage) ---
    return (
        <div className={`quiz-modal-overlay ${bgImage ? 'has-bg-image' : ''}`} style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}>
            <button className="quiz-modal-close quiz-modal-close--fixed" onClick={handleBack} title="Quitter">
                <X size={22} strokeWidth={2} />
            </button>
            <ThemeToggle className="quiz-theme-toggle" />
            <div className="quiz-page">
            <div className="quiz-page-scroll">
                {(design.header_image || design.scope_label) && (
                    <div className="quiz-scope-banner" style={{ '--scope-accent': themeColor }}>
                        {design.header_image ? (
                            <div className="quiz-scope-banner-hero">
                                <img src={design.header_image} alt="" className="quiz-scope-banner-img" />
                                <div className="quiz-scope-banner-gradient" />
                            </div>
                        ) : (
                            <div className="quiz-scope-banner-hero" style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}dd)`, minHeight: 60 }} />
                        )}
                        <div className="quiz-scope-banner-content">
                            <div className="quiz-scope-banner-info">
                                <span className="quiz-scope-banner-title">{design.scope_label}</span>
                            </div>
                            {sousSujet.categorie && <span className="quiz-scope-banner-cat">{sousSujet.categorie}</span>}
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="quiz-header">
                    <div className="quiz-header-top">
                        <h2>{sousSujet.label || 'Entraînement'}</h2>
                        {isGiftChallenge ? (
                            <span className="quiz-mode-badge" style={{ background: 'linear-gradient(135deg, #ec4899, #f59e0b)', color: '#fff' }}>
                                <Trophy size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />Mode concours
                            </span>
                        ) : (
                            <span className="quiz-mode-badge apprentissage">Entraînement libre</span>
                        )}
                    </div>
                    <div className="quiz-meta">
                        {docPath && (
                            <button className="qcm-popup-doc-fab" style={{ position: 'static', width: 'auto', height: 'auto', borderRadius: 6, padding: '4px 10px', fontSize: '0.82em' }}
                                onClick={() => setShowDoc(true)} title={sousSujet.doc_name || 'Voir le document source'}>
                                <FileText size={14} /> Document
                            </button>
                        )}
                        <ProgressBar current={currentIndex + 1} total={total} />
                    </div>
                </div>

                {/* Mini-nav dots */}
                {total > 1 && (
                    <div className="quiz-mini-nav">
                        <button className="mini-nav-arrow" onClick={() => scrollDots('left')}>
                            <ChevronLeft size={18} />
                        </button>
                        <div className="mini-nav-dots" ref={dotsRef}>
                            {questions.map((q, i) => {
                                const hasAnswer = !!answers[q.id];
                                const isCurrent = i === currentIndex;
                                // Mode concours : pas de feedback correct/incorrect (corrections masquées).
                                const isCorrect = !isGiftChallenge && hasAnswer && answers[q.id] === q.bonne_reponse;
                                const isIncorrect = !isGiftChallenge && hasAnswer && !isCorrect;
                                return (
                                    <button key={q.id}
                                        className={`dot ${hasAnswer ? 'answered' : ''} ${isCurrent ? 'active' : ''} ${isCorrect ? 'correct' : ''} ${isIncorrect ? 'incorrect' : ''}`}
                                        onClick={() => goToQuestion(i)}>
                                        {i + 1}
                                    </button>
                                );
                            })}
                        </div>
                        <button className="mini-nav-arrow" onClick={() => scrollDots('right')}>
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}

                {/* Question */}
                {currentQ && (
                    <>
                        <QuestionCard
                            question={currentQ}
                            selectedAnswer={given}
                            onAnswer={(id) => setAnswer(currentQ.id, id)}
                            showResult={isGiftChallenge ? false : answered}
                            correctAnswer={isGiftChallenge ? undefined : correct}
                            apprentissageFeedback={!isGiftChallenge}
                        />
                        {!isGiftChallenge && answered && currentQ.explication && (
                            <div className="quiz-explication" style={{ margin: '0 0 16px' }}>
                                <RichContent blocks={currentQ.explication} />
                            </div>
                        )}
                    </>
                )}

                {/* Navigation */}
                <div className="quiz-nav">
                    <button className="btn btn-ghost btn-sm" onClick={goPrev} disabled={currentIndex === 0}>
                        <ChevronLeft size={14} /> Précédent
                    </button>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>{currentIndex + 1} / {total}</span>
                    <button className="btn btn-primary btn-sm" onClick={goNext} disabled={!answered}>
                        {currentIndex === total - 1 ? 'Terminer' : <>Suivant <ChevronRight size={14} /></>}
                    </button>
                </div>

                {/* Pendant le quiz, on garde le lecteur épuré : les CTA (voir la thématique /
                    créer un compte) sont regroupés sur l'écran de résultat uniquement. */}
                {!isCampaign && (
                    <p style={{ fontSize: '0.72em', color: 'var(--text-muted)', textAlign: 'center', marginTop: 24 }}>
                        Entraînement libre — aucune donnée n'est collectée ni sauvegardée.
                    </p>
                )}
                </div>
            </div>

            {showDoc && docPath && createPortal(
                <DocumentViewer src={docPath} name={sousSujet.doc_name} onClose={() => setShowDoc(false)} />,
                document.body
            )}
        </div>
    );
}
